import React from 'react';
import { makeStyles, fade } from '@material-ui/core/styles';
import { Typography, IconButton, Button, Tooltip, Chip } from '@material-ui/core';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import LocalOfferOutlinedIcon from '@material-ui/icons/LocalOfferOutlined';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import NotificationsActiveOutlinedIcon from '@material-ui/icons/NotificationsActiveOutlined';
import clsx from 'clsx';
import { getCampaignStatus } from '../helpers';

const useStyles = makeStyles((theme) => ({
	'@keyframes cardIn': {
		from: { opacity: 0, transform: 'translateY(7px)' },
		to: { opacity: 1, transform: 'none' }
	},
	card: {
		display: 'flex',
		alignItems: 'flex-start',
		gap: theme.spacing(1.5),
		padding: theme.spacing(1.5, 2),
		border: `1px solid ${theme.palette.divider}`,
		borderRadius: 12,
		backgroundColor: theme.palette.background.paper,
		cursor: 'pointer',
		animation: '$cardIn 280ms cubic-bezier(0.16, 1, 0.3, 1) both',
		transition: theme.transitions.create(['transform', 'box-shadow', 'border-color'], { duration: 180 }),
		'&:hover': {
			transform: 'translateY(-1px)',
			boxShadow: `0 6px 16px ${fade('#000', 0.12)}`,
			borderColor: fade('#FEF200', 0.9)
		},
		'&:hover $actions': {
			opacity: 1
		}
	},
	iconWrap: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: 40,
		height: 40,
		borderRadius: 10,
		flexShrink: 0,
		color: 'rgba(0,0,0,0.72)',
		backgroundColor: '#FEF200'
	},
	body: {
		flex: 1,
		minWidth: 0
	},
	nameRow: {
		display: 'flex',
		alignItems: 'center',
		flexWrap: 'wrap',
		gap: theme.spacing(1),
		marginBottom: 2
	},
	name: {
		fontWeight: 700,
		letterSpacing: '-0.01em'
	},
	meta: {
		color: theme.palette.text.secondary,
		fontSize: 12.5,
		marginBottom: theme.spacing(0.75)
	},
	chips: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: theme.spacing(0.5)
	},
	chip: {
		height: 20,
		fontSize: 10.5,
		fontFamily: 'monospace',
		backgroundColor: fade(theme.palette.text.primary, 0.05)
	},
	plainChip: {
		fontFamily: 'inherit'
	},
	notifyChip: {
		fontFamily: 'inherit',
		color: theme.palette.type === 'dark' ? '#7ee2a8' : '#1b7a43',
		backgroundColor: fade(theme.palette.success.main, theme.palette.type === 'dark' ? 0.18 : 0.12)
	},
	notifyIcon: {
		fontSize: 12
	},
	statusPill: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: theme.spacing(0.5),
		height: 20,
		paddingLeft: theme.spacing(0.75),
		paddingRight: theme.spacing(1),
		borderRadius: 10,
		fontSize: 11,
		fontWeight: 700
	},
	statusDot: {
		fontSize: 8
	},
	statusActive: {
		color: theme.palette.type === 'dark' ? '#7ee2a8' : '#1b7a43',
		backgroundColor: fade(theme.palette.success.main, theme.palette.type === 'dark' ? 0.18 : 0.14)
	},
	statusUpcoming: {
		color: theme.palette.type === 'dark' ? '#8ecbff' : '#0d5ca8',
		backgroundColor: fade(theme.palette.info.main, theme.palette.type === 'dark' ? 0.2 : 0.14)
	},
	statusExpired: {
		color: theme.palette.text.secondary,
		backgroundColor: fade(theme.palette.text.primary, 0.07)
	},
	statusDraft: {
		color: theme.palette.type === 'dark' ? '#ffcf7d' : '#8a5b00',
		border: `1px dashed ${fade('#c98b00', 0.6)}`,
		backgroundColor: 'transparent'
	},
	right: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-end',
		gap: theme.spacing(0.5),
		flexShrink: 0
	},
	actions: {
		display: 'flex',
		gap: theme.spacing(0.25),
		opacity: 0.35,
		transition: theme.transitions.create('opacity', { duration: 150 })
	},
	iconBtn: {
		padding: 6,
		color: theme.palette.text.secondary,
		'&:hover': {
			color: theme.palette.text.primary,
			backgroundColor: fade(theme.palette.text.primary, 0.08)
		}
	},
	deleteBtn: {
		'&:hover': {
			color: theme.palette.error.main,
			backgroundColor: fade(theme.palette.error.main, 0.12)
		}
	},
	viewReportBtn: {
		fontSize: 11,
		textTransform: 'none',
		padding: theme.spacing(0.25, 1)
	},
	d0: { animationDelay: '0ms' },
	d1: { animationDelay: '35ms' },
	d2: { animationDelay: '70ms' },
	d3: { animationDelay: '105ms' },
	d4: { animationDelay: '140ms' },
	d5: { animationDelay: '175ms' },
	d6: { animationDelay: '210ms' },
	d7: { animationDelay: '245ms' }
}));

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const formatDate = (d) => {
	if (!d) {
		return '—';
	}
	const parts = d.split('-');
	return `${parseInt(parts[2])} ${MONTH_LABELS[parseInt(parts[1]) - 1]} ${parts[0]}`;
};

export default function PromoCard({ row, language, onEdit, onDelete, index }) {
	const classes = useStyles();
	const status = getCampaignStatus(row);
	const statusClass = {
		active: classes.statusActive,
		upcoming: classes.statusUpcoming,
		expired: classes.statusExpired,
		draft: classes.statusDraft
	}[status];
	const statusLabel = {
		active: language.active || 'Active',
		upcoming: language.upcoming || 'Upcoming',
		expired: language.expired || 'Expired',
		draft: language.draft || 'Draft'
	}[status];
	const skuCodes = row.sku_codes ? row.sku_codes.split(',') : [];
	const chains = row.chain_names ? row.chain_names.split(',') : [];

	return (
		<div className={clsx(classes.card, classes[`d${Math.min(index, 7)}`])} onClick={() => onEdit(row)}>
			<span className={classes.iconWrap}>
				<LocalOfferOutlinedIcon />
			</span>
			<div className={classes.body}>
				<div className={classes.nameRow}>
					<Typography variant="subtitle1" className={classes.name}>{row.name}</Typography>
					<span className={clsx(classes.statusPill, statusClass)}>
						<FiberManualRecordIcon className={classes.statusDot} />
						{statusLabel}
					</span>
				</div>
				<Typography className={classes.meta}>
					{(row.brand_name || '—')} · {formatDate(row.start_date)} – {formatDate(row.end_date)} · {row.outlet_count} {language.outlets_unit}
				</Typography>
				<div className={classes.chips}>
					{skuCodes.slice(0, 3).map((code) => (
						<Chip key={code} size="small" label={code} className={classes.chip} />
					))}
					{skuCodes.length > 3 && (
						<Tooltip title={skuCodes.slice(3).join(', ')} arrow>
							<Chip size="small" label={`+${skuCodes.length - 3}`} className={clsx(classes.chip, classes.plainChip)} />
						</Tooltip>
					)}
					{chains.map((chain) => (
						<Chip key={chain} size="small" label={chain} className={clsx(classes.chip, classes.plainChip)} />
					))}
					<Chip size="small" label={`${row.task_count} ${language.tasks_unit}`} className={clsx(classes.chip, classes.plainChip)} />
					{row.task_start_date ? (
						<Chip
							size="small"
							icon={<NotificationsActiveOutlinedIcon className={classes.notifyIcon} />}
							label={`${language.notify} ${formatDate(row.task_start_date)}`}
							className={clsx(classes.chip, classes.notifyChip)}
						/>
					) : null}
				</div>
			</div>
			<div className={classes.right}>
				<div className={classes.actions}>
					<Tooltip title={language.edit || 'Edit'} arrow>
						<IconButton className={classes.iconBtn} onClick={(e) => { e.stopPropagation(); onEdit(row); }}>
							<EditOutlinedIcon fontSize="small" />
						</IconButton>
					</Tooltip>
					<Tooltip title={language.delete} arrow>
						<IconButton className={clsx(classes.iconBtn, classes.deleteBtn)} onClick={(e) => { e.stopPropagation(); onDelete(row); }}>
							<DeleteOutlineIcon fontSize="small" />
						</IconButton>
					</Tooltip>
				</div>
				<Tooltip title={language.coming_soon} arrow>
					<span onClick={(e) => e.stopPropagation()}>
						<Button size="small" variant="outlined" disabled className={classes.viewReportBtn}>
							{language.view_report}
						</Button>
					</span>
				</Tooltip>
			</div>
		</div>
	);
}
